import { useQuery } from "@apollo/client";
import { Delete, Edit, Unarchive } from "@mui/icons-material";
import { Button, Typography } from "@mui/material";
import { ReactElement, useState } from "react";
import { FormattedMessage } from "react-intl";

import {
  GetCampaignMetadataFieldsForTenantQuery,
  ArchiveMetadataFieldMutation,
  ArchiveMetadataFieldMutationVariables,
  ReactivateMetadataFieldMutation,
  ReactivateMetadataFieldMutationVariables,
  MetadataFieldsFragment,
  MetadataStringFieldFragment,
  MetadataEnumFieldFragment,
  MetadataBoolFieldFragment,
  MetadataEnumMultiFieldFragment,
  MetadataStateFieldFragment,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import Table, { TableBox, RowActions } from "~frontend/components/Table";
import { EditFieldDialog } from "~frontend/components/campaignMetadata/EditFieldDialog";
import {
  ARCHIVE_METADATA_FIELD,
  REACTIVATE_METADATA_FIELD,
  GET_TENANT_METADATA_FIELDS,
} from "~frontend/graphql/campaignMetadata";
import { palette } from "~frontend/theme";
import useMutation from "~frontend/util/useMutation";

import { mapTypenameToLabel } from "./common";

const description = `Use structured metadata fields to organize campaigns across your
  subaccounts. As an Organization Owner you can create the metadata
  structure that will be available to all campaigns in all
  subaccounts, and Subaccount Admin or above can set the values for
  each metadata field on campaigns they create.`;

const NoMetadataConfigured = (
  <FormattedMessage
    defaultMessage="There are no metadata fields configured for this organization."
    description="Empty state message for a tenant with no metadata fields"
  />
);

function ColumnTypography({
  archived,
  value,
}: {
  archived: boolean;
  value: string;
}): ReactElement {
  if (archived) {
    return (
      <Typography
        sx={{
          fontStyle: "italic",
          color: palette.darkGray,
        }}
      >
        {value}
      </Typography>
    );
  }
  return <Typography>{value}</Typography>;
}

function stringArrayText(listValue: string[]): string {
  const value = listValue.join(", ");
  return value.length > 15 ? `${value.slice(0, 15)}...` : value;
}

function createMetadataDisplayValue(row: MetadataFieldsFragment): string {
  switch (row.__typename) {
    case "CampaignMetadataStringField":
      return (row as MetadataStringFieldFragment).defaultStringValue || "-";
    case "CampaignMetadataEnumField":
      return (row as MetadataEnumFieldFragment).defaultEnumValue || "-";
    case "CampaignMetadataBoolField": {
      const boolValue = (row as MetadataBoolFieldFragment).defaultBoolValue;
      if (boolValue === null) {
        return "-";
      } else if (boolValue) {
        return "True";
      } else {
        return "false";
      }
    }
    case "CampaignMetadataEnumMultiField": {
      const listValue = (row as MetadataEnumMultiFieldFragment)
        .defaultEnumMultiValue;
      if (listValue.length === 0) {
        return "-";
      }
      return stringArrayText(listValue);
    }
    case "CampaignMetadataStateField": {
      const listValue = (row as MetadataStateFieldFragment).defaultStateValue;
      if (listValue.length === 0) {
        return "-";
      }
      return stringArrayText(listValue);
    }
    case "CampaignMetadataUserListField": {
      return "-";
    }
  }
}

const columns = [
  {
    key: "name",
    title: "Field Name",
    renderValue: (row: MetadataFieldsFragment) => (
      <FlexBox gap={1} alignItems="center">
        <ColumnTypography
          key={row.id}
          archived={row.archived}
          value={row.name}
        />
        {row.archived && (
          <Typography
            fontWeight={500}
            sx={{
              backgroundColor: palette.lightGray,
              p: 0.5,
              color: palette.darkGray,
            }}
          >
            Archived
          </Typography>
        )}
      </FlexBox>
    ),
  },
  {
    key: "type",
    title: "Field Type",
    renderValue: (row: MetadataFieldsFragment) => (
      <ColumnTypography
        key={row.id}
        archived={row.archived}
        value={mapTypenameToLabel(row.__typename)}
      />
    ),
  },
  {
    key: "default",
    title: "Default Value",
    renderValue: (row: MetadataFieldsFragment) => {
      const value = createMetadataDisplayValue(row);
      return <ColumnTypography archived={row.archived} value={value} />;
    },
  },
  {
    key: "required",
    title: "Required?",
    renderValue: (row: MetadataFieldsFragment) => (
      <ColumnTypography
        key={row.id}
        archived={row.archived}
        value={row.required ? "Yes" : "No"}
      />
    ),
  },
];

export function CampaignMetadataFieldsSection(): ReactElement {
  const { data, loading, error } =
    useQuery<GetCampaignMetadataFieldsForTenantQuery>(
      GET_TENANT_METADATA_FIELDS
    );
  return (
    <>
      <Typography variant="display0Bold" sx={{ pb: 2, pt: 2 }} component="div">
        Campaign Metadata
      </Typography>
      <Typography variant={"h2"} sx={{ pb: 2 }}>
        {description}
      </Typography>
      <AsyncWrapper loading={loading} errors={error} data={data}>
        {data => (
          <CampaignMetadataFieldsTable
            data={data.campaignMetadataFieldsForTenant}
          />
        )}
      </AsyncWrapper>
    </>
  );
}

export function CampaignMetadataFieldsTable({
  data,
}: {
  data: MetadataFieldsFragment[];
}): ReactElement {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editField, setEditField] = useState<MetadataFieldsFragment | null>();

  const [archiveField] = useMutation<
    ArchiveMetadataFieldMutation,
    ArchiveMetadataFieldMutationVariables
  >(ARCHIVE_METADATA_FIELD, {
    onErrorSnackbarMessage: "Error removing field field.",
    refetchQueries: [GET_TENANT_METADATA_FIELDS],
    awaitRefetchQueries: true,
  });

  const [reactivateField] = useMutation<
    ReactivateMetadataFieldMutation,
    ReactivateMetadataFieldMutationVariables
  >(REACTIVATE_METADATA_FIELD, {
    onErrorSnackbarMessage: "Error reactivating field field.",
    refetchQueries: [GET_TENANT_METADATA_FIELDS],
    awaitRefetchQueries: true,
  });

  const archive = async (id: string): Promise<void> => {
    await archiveField({
      variables: {
        input: { id },
      },
    });
  };

  async function reactivate(id: string): Promise<void> {
    await reactivateField({
      variables: {
        input: { id },
      },
    });
  }

  function renderRowActions(
    row: MetadataFieldsFragment
  ): RowActions<MetadataFieldsFragment> {
    if (!row.archived) {
      return [
        {
          id: "edit",
          type: "button",
          icon: <Edit />,
          tooltipLabel: "Edit",
          onClick: (row: MetadataFieldsFragment) => {
            setEditField(row);
          },
        },
        {
          id: "delete",
          type: "button",
          icon: <Delete />,
          tooltipLabel: "Delete",
          onClick: async (row: MetadataFieldsFragment) => await archive(row.id),
        },
      ];
    }

    return [
      {
        id: "reactivate",
        type: "button",
        icon: <Unarchive />,
        tooltipLabel: "Reactivate",
        onClick: async (row: MetadataFieldsFragment) =>
          await reactivate(row.id),
      },
    ];
  }

  const activeFields = data.filter(field => !field.archived);
  const inactiveFields = data
    .filter(field => field.archived)
    .sort((a, b) => a.name.localeCompare(b.name));

  const rows = [...activeFields, ...inactiveFields].slice(
    page * rowsPerPage,
    (page + 1) * rowsPerPage
  );

  return (
    <TableBox sx={{ pb: 1 }}>
      <Table
        columns={columns}
        rows={rows}
        emptyState={NoMetadataConfigured}
        totalCount={data.length}
        rowsPerPage={rowsPerPage}
        onChangeRowsPerPage={setRowsPerPage}
        page={page}
        onChangePage={setPage}
        rowActions={renderRowActions}
      />
      <FlexBox justifyContent="flex-start" sx={{ pb: 1 }}>
        <AddFieldBtn />
      </FlexBox>
      {Boolean(editField) && (
        <EditFieldDialog
          open
          closeDialog={() => setEditField(null)}
          editField={editField as MetadataStateFieldFragment}
        />
      )}
    </TableBox>
  );
}

function AddFieldBtn() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button variant="contained" onClick={() => setOpen(true)}>
        + Add Metadata Field
      </Button>
      <EditFieldDialog open={open} closeDialog={() => setOpen(false)} />
    </>
  );
}
